import { useState } from 'react';
import { CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { AgentMessage } from '../lib/types';

// An answer as the question stores it, and how it reads.
interface Option { value: unknown; label: string }

/** Fired when an answer is saved from the chat, so a Summary showing that question reloads it. */
export const FIELD_SAVED_EVENT = 'navaris:field-saved';

// Answers an agent proposed for one of the company's questions. Nothing is
// saved until the person picks one; picking saves it exactly as typing it on
// the Summary would.
export default function FieldProposal({ message }: { message: AgentMessage }) {
    const { authFetch } = useAuth();
    const { refresh } = useWorkspace();
    const [saving, setSaving] = useState<number | null>(null);
    const [saved, setSaved] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    let proposal: { field_id: string; options: (Option | string)[] } | null = null;
    try { proposal = JSON.parse(message.content); } catch {}
    if (!proposal?.field_id || !proposal.options?.length) return null;
    const fieldId = proposal.field_id;
    // A proposal stored as plain text is its own label.
    const options: Option[] = proposal.options.map(o => typeof o === 'string' ? { value: o, label: o } : o);

    const use = async (i: number) => {
        setSaving(i);
        setError(null);
        try {
            const res = await authFetch(`/workspace/fields/${fieldId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: options[i].value }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.detail || 'That answer could not be saved.');
            }
            setSaved(i);
            window.dispatchEvent(new CustomEvent(FIELD_SAVED_EVENT, { detail: { fieldId } }));
            refresh();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'That answer could not be saved.');
        } finally {
            setSaving(null);
        }
    };

    return (
        <div className="flex justify-start">
            <div className="max-w-[90%] w-fit bg-white border border-softgrey rounded-xl p-3 space-y-2">
                <p className="flex items-center gap-1.5 text-xs font-medium text-coolslate">
                    <Sparkles className="w-3.5 h-3.5 shrink-0 text-brand-dark" />
                    Suggested answers to "{message.title}"
                </p>
                {options.map((o, i) => (
                    <div key={i} className="flex items-center gap-3">
                        <p className={`min-w-0 text-sm text-graphite rounded-lg border px-3 py-2 ${
                            saved === i ? 'border-sage/50 bg-sage/10' : 'border-softgrey'}`}>{o.label}</p>
                        {saved === i ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-sage shrink-0">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Saved
                            </span>
                        ) : (
                            <button
                                type="button"
                                onClick={() => use(i)}
                                disabled={saving !== null}
                                className="inline-flex items-center gap-1 text-xs font-medium text-brand-dark hover:underline disabled:opacity-50 shrink-0"
                            >
                                {saving === i && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                Use this
                            </button>
                        )}
                    </div>
                ))}
                {error && <p className="text-xs text-state-error">{error}</p>}
            </div>
        </div>
    );
}
